import {RequestFormType} from "../../../app/tools/request-forms2/const/const"
import RequestFormSettings from "./settings/settings"
import {ModelInterface} from "../../data-structures/collection"
import {RequestFormConfig} from "../../../app/tools/request-forms2/form-config"
import {loadFormMissingData} from "./functions/load-form-missing-data"
import CustomFieldsManager from "../../gd/manager/custom-fields-manager"
import ApiRequestForm from "../../gd/api/request-form"
import ModelProxy from "../model-proxy"
import openFormAction from "../../../app/tools/request-forms2/actions/open-form"
import editFormAction from "../../../app/tools/request-forms2/actions/edit-form"
import CUSTOM_FIELDS_V1 from "../../gd/const/fields/custom-fields-v1"
import {ViewType} from "../../gd/const/views/view-type"
import {ProjectViewAccess} from "../project-view/@const"
import {CustomFieldScope} from "../custom-field/@const";
import SessionCustomField from "../custom-field/session-custom-field";
import ProjectUserSession from "../project-user/project-user-session";
import RequestFormField from "./settings/field"

// const debug = "RequestForm"
const debug = false

export default class RequestForm implements ModelInterface {

    id                      : ID            // request form ID
    companyId               : ID            // the company to which the form belongs
    type                    : RequestFormType
    // access              : any        // pust' budet dumau ponadobitsya
    // sharedViewId        : ID         // shared view id used with public (forms) only
    publicKey               : string        // sharedView.public_key shared.goodday.work/PUBLIC_KEY

    sortPosition            : number        // vopros - kak mi sortiruem forms / request separately pofig esli odinkaovie dlyaraznih types ?


    // Task creation
    projectId               : ID            // project this form belongs to and project where we create new tasks if taskProjectId is not specified
    private _taskProjectId  : ID            // project where we create new tasks
    taskTypeId              : ID            // type of task that will be created when the form is submitted (REQUIRED - first project task type by default)
    assignToUserId          : ID | null     // the user task will be assigned to when the form is submitted

    name                    : string        // Sytem name we display in management interface
    description?            : string        // System description we use in managment interafece *used for request only?

    settings                : RequestFormSettings

    // History - poka pod voprosom
    // momentCreated?      : Moment
    // createdByUserId?    : ID
    // lastUpdatedByUserId?: ID
    // lastUpdatedMoment?  : Moment

    isDeleted           : boolean
    submissionsCount    : number

    updatedFields       : Set<string>

    constructor(data:any, options={}) {

        this.id                 = _.get(data, 'id', null)
        this.companyId          = _.get(data, 'companyId', null)
        this.type               = _.get(data, 'type', RequestFormType.REQUEST)
        this.publicKey          = _.get(data, 'publicKey', null)
        this.sortPosition       = _.get(data, 'sortPosition', 0)

        this.projectId          = _.get(data, 'projectId', null)
        this._taskProjectId     = _.get(data, 'taskProjectId', null)
        this.taskTypeId         = _.get(data, 'taskTypeId', null)
        this.assignToUserId     = _.get(data, 'assignToUserId', null)

        this.name               = _.get(data, 'name', null)
        this.description        = _.get(data, 'description', undefined)

        this.settings           = new RequestFormSettings(this.type, _.get(data, 'settings', {}))

        this.isDeleted          = _.get(data, 'isDeleted', false)
        this.submissionsCount   = _.get(data, 'submissionsCount', 0)

        this.updatedFields      = new Set()

        const useProxy = _.get(options, 'useProxy', true)
        return useProxy ? new Proxy(this, ModelProxy) : this;
    }

    /**
     * Initializes a RequestForm instance from a given config.
     * Loads any missing data required for the form fields (e.g., CRM data).
     * Ensures that the session is updated with any required missing data before returning the form.
     * @param config The configuration object for the request form.
     * @returns A Promise that resolves to a new RequestForm instance.
     */
    static async initFromConfig(config:RequestFormConfig):Promise<any> {
        // Load any missing data required for the form
        await loadFormMissingData(config)

        debug && console.log(debug, "initFromConfig", config)

        // Create a new RequestForm instance using the provided config
        const requestForm = new RequestForm(config)

        // Prepare an object to track what additional data needs to be loaded (e.g., CRM)
        let missingData: any = {}

        // Check each field in the form to see if it requires additional data
        _.map(requestForm.settings.fields, (field: RequestFormField) => {
            switch (field.taskFieldId) {
                case 'crmAccountId':
                case 'crmContactIds':
                    // If a CRM field is found, mark CRM data as required
                    missingData.crm = true
                    break
            }
        })

        // Reset or load any missing session data as needed (e.g., CRM info)
        await gd.api.Session.resetMissing(missingData).promise()

        // Return the initialized RequestForm instance
        return requestForm
    }

    applyDelta(data:any) {

        let isChanged = false

        _.mapKeys(data, (value ,key)=>{
            if (key === 'type') return

            switch (key) {
                case 'settings':
                    this.settings = new RequestFormSettings(this.type, value)
                    isChanged = true
                    break

                // case 'crmIntegration':
                //     this.crmIntegration = new RequestFormCrmIntegration(value)
                //     isChanged = true
                //     break

                default:
                    // @ts-ignore
                    this[key] = value
                    isChanged = true
            }

            debug && console.log(debug, "applyDelta key=", key, "value=", value)

            isChanged = true
        })

        return isChanged
    }

    get isRequest() {
        return this.type === RequestFormType.REQUEST
    }

    get isForm() {
        return this.type === RequestFormType.FORM
    }

    get publicURL() {
        return gdConfig.sharedViewPath + this.publicKey
    }

    get embedCode() {
        return '<iframe src="'+this.publicURL+'"/>'
    }

    get availableTaskTypesIds() {
        // based on project
        return gd.session.projects.get(this.taskProjectId)!.getInheritedTaskTypes()
    }

    get availableAssignToUsersIds():ID[] {
        // based on project
        const projectUsers = gd.session.projectUsers.filterByProject(this.taskProjectId)
        return _.map(projectUsers.models, (pu:ProjectUserSession)=>{
            return pu.userId
        })
    }

    getDefaultTaskType() {
        return this.availableTaskTypesIds[0]
    }

    get availableCustomFieldsIds():ID[] {
        return _(CustomFieldsManager.getProjectTaskListCustomFields(
            [this.taskTypeId],
            this.taskProjectId,
            [CustomFieldScope.TASK]))
            .filter((customField:SessionCustomField) => {
                return customField.type !== CUSTOM_FIELDS_V1.type.FORMULA
            })
            .map(customField => customField.id)
            .value()
    }

    get taskProjectId() {
        return this._taskProjectId || this.projectId
    }

    set taskProjectId(value) {
        this._taskProjectId = value
        // if current task type is not among available then change it to first from available
        const taskTypesIds = this.availableTaskTypesIds
        if (!taskTypesIds.includes(this.taskTypeId)) {
            this.taskTypeId = taskTypesIds[0]
            this.updatedFields.add('taskTypeId')
        }

        // if current assigned to user is not among available then change it to first from available
        const userIds = this.availableAssignToUsersIds

        if (!this.assignToUserId || !userIds.includes(this.assignToUserId)) {
            this.assignToUserId = null
            this.updatedFields.add('assignToUserId')
        }

    }

    get projectViewConfig() {
        return {
            systemType  : ViewType.PROJECT_REQUEST_FORMS,
            formId      : this.id,
            name        : this.name,
            access      : ProjectViewAccess.PUBLIC,
            isPinned    : true,
        }
    }

    open() {
        debug && console.log(debug, 'open', this.type, this)
        openFormAction(this)
    }

    edit() {
        debug && console.log(debug, 'edit', this.type, this)
        editFormAction(this)
    }

    // Data management
    async save() {
        debug && console.log(debug, 'save', this.updatedFields, this.settings.updatedFields)

        const params:any = {}

        this.updatedFields.forEach((fieldName:string) => {
            // @ts-ignore
            params[fieldName] = this[fieldName]
        })
        this.updatedFields.clear()

        // check if settings is modified
        if (this.settings.updatedFields.size > 0) {
            params.settings = this.settings.toJSON()
            this.settings.updatedFields.clear()
        }

        if (params && _.size(params)>0) return await ApiRequestForm.updateForm(this.id, params)

        return true
    }

    delete() {
        // make remove request
    }

    // for form duplication
    clone():RequestForm {
        return new RequestForm(this.toJSON())
    }

    toJSON() {
        let res: JSONObject = {
            id                  : this.id,
            companyId           : this.companyId,
            type                : this.type,
            sortPosition        : this.sortPosition,
            projectId           : this.projectId,
            taskProjectId       : this._taskProjectId,
            taskTypeId          : this.taskTypeId,
            assignToUserId      : this.assignToUserId,
            name                : this.name,
            description         : this.description || null,

            settings            : this.settings.toJSON()
        }
        return res
    }

    toOption():Option {
        return {
            label       : this.name,
            value       : this.id,
            icon        : 'i5-request'
        }
    }
}
